import { BucketId, FileMetadata } from '../types/File';
import { DirectUploadService } from './directUploadService';

/**
 * Service for uploading files to the Decentraland File Manager.
 *
 * Requests go to the same-origin `/api` path (served by the Cloudflare Pages Function,
 * which authenticates via the Cloudflare Access JWT); the browser sends no credentials
 * of its own.
 */
class UploadService {
  private baseUrl: string;
  private directUploadService: DirectUploadService;

  constructor() {
    this.baseUrl = '/api';
    this.directUploadService = new DirectUploadService(this.baseUrl);
  }

  /**
   * Uploads a single file to the given bucket.
   */
  async uploadFile(
    file: File,
    bucket: BucketId,
    onProgress?: (progress: number) => void,
    signal?: AbortSignal
  ): Promise<FileMetadata> {
    try {
      const result = await this.directUploadService.uploadLargeFile(file, bucket, onProgress, signal);

      return {
        key: result.key,
        url: result.url,
        size: file.size,
        type: file.type,
        lastModified: new Date().toISOString(),
        filename: file.name,
      };
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw error;
        }
        if (error.message.includes('CORS') || error.message.includes('Failed to fetch')) {
          throw new Error('Network error: unable to reach the file management server. Please try again.');
        }
        throw new Error(`Upload failed: ${error.message}`);
      }
      throw new Error('Upload failed: Unknown error');
    }
  }
}

export const uploadService = new UploadService();
